import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Info, Loader2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { montarPayloadCanvas } from '@/lib/organogramaGeradorCanvas';
import { gerarOrganograma, type DadosGeracao } from '@/lib/organogramaGerador';
import { fichaDoUsuario, LIMITE_NOMES, montarAvisos } from '@/lib/organogramaGeradorAvisos';
import { organogramasService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

interface GerarOrganogramaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Superadmin: empresa selecionada no filtro. Admin: deixe vazio (o backend usa a empresa ativa). */
  empresaId?: string;
}

const nomePadrao = () => `Organograma gerado — ${new Date().toLocaleDateString('pt-BR')}`;

/** Botão com cara de link dos atalhos da revisão. */
const CLASSE_ATALHO = 'font-medium text-primary underline-offset-2 hover:underline disabled:opacity-50';

/**
 * Nome digitado guardado ao seguir um atalho (o diálogo fecha para ir corrigir): ao reabrir na **mesma
 * empresa**, nesta aba do navegador e em até 15 minutos, o nome volta (uma vez). Passado esse prazo — a pessoa
 * não voltou da correção — vale o nome padrão. sessionStorage pode falhar (modo privado): aí só não guarda.
 */
const VALIDADE_NOME_GUARDADO_MS = 15 * 60 * 1000;
const chaveNomeGuardado = (empresaId: string) => `kora_gerar_organograma_nome_${empresaId}`;
const lerNomeGuardado = (empresaId: string): string | null => {
  try {
    const bruto = sessionStorage.getItem(chaveNomeGuardado(empresaId));
    sessionStorage.removeItem(chaveNomeGuardado(empresaId));
    if (!bruto) return null;
    const guardado = JSON.parse(bruto) as { nome?: unknown; em?: unknown };
    const recente = typeof guardado.em === 'number' && Date.now() - guardado.em < VALIDADE_NOME_GUARDADO_MS;
    return recente && typeof guardado.nome === 'string' ? guardado.nome : null;
  } catch {
    return null;
  }
};
const guardarNome = (empresaId: string, nome: string) => {
  try {
    sessionStorage.setItem(chaveNomeGuardado(empresaId), JSON.stringify({ nome, em: Date.now() }));
  } catch {
    // sem armazenamento: o nome volta ao padrão, sem prejuízo
  }
};


export function GerarOrganogramaDialog({ open, onOpenChange, empresaId }: GerarOrganogramaDialogProps) {
  const navigate = useNavigate();
  const { empresaAtiva } = useAuth();
  // Empresa do nome guardado: a do filtro (superadmin) ou a ativa (admin) — trocar de empresa não carrega o nome.
  // Sem empresa resolvida, o nome não é guardado (nada de chave genérica compartilhada).
  const empresaDoNome = empresaId || empresaAtiva?.id || null;
  const [dados, setDados] = useState<DadosGeracao | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGerando, setIsGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [nome, setNome] = useState(nomePadrao);
  // Nome padrão de quando o diálogo abriu: perto da meia-noite o "padrão de agora" já seria o do dia seguinte.
  const nomePadraoAoAbrir = useRef(nomePadrao());

  useEffect(() => {
    if (!open) return;
    let cancelado = false;
    nomePadraoAoAbrir.current = nomePadrao();
    setNome((empresaDoNome && lerNomeGuardado(empresaDoNome)) || nomePadraoAoAbrir.current);
    setDados(null);
    setErro(null);
    setIsLoading(true);
    organogramasService
      .getDadosGeracao(empresaId || undefined)
      .then((resposta) => {
        if (cancelado) return;
        if (resposta.success && resposta.data) setDados(resposta.data);
        else setErro(resposta.error || 'Não foi possível carregar os dados dos cadastros.');
      })
      .finally(() => {
        if (!cancelado) setIsLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [open, empresaId, empresaDoNome]);

  const resultado = useMemo(() => (dados ? gerarOrganograma(dados) : null), [dados]);
  const avisos = useMemo(() => (resultado ? montarAvisos(resultado) : []), [resultado]);

  const totalCards = resultado ? resultado.nos.length : 0;
  const totalDepartamentos = resultado ? resultado.nos.filter((no) => no.tipo === 'departamento').length : 0;

  /** Fecha a revisão e leva até a tela onde o problema se corrige; ao voltar, gerar de novo relê os cadastros. */
  const irPara = (destino: string) => {
    if (isGerando) return;
    if (empresaDoNome && nome.trim() && nome !== nomePadraoAoAbrir.current) guardarNome(empresaDoNome, nome);
    onOpenChange(false);
    navigate(destino);
  };

  const handleOpenChange = (next: boolean) => {
    if (isGerando) return;
    onOpenChange(next);
  };

  const handleGerar = async () => {
    if (!resultado || !nome.trim()) return;
    setIsGerando(true);
    setErro(null);
    try {
      const criado = await organogramasService.create({
        empresa_id: empresaId || undefined,
        nome: nome.trim(),
        is_principal: false,
      });
      if (!criado.success || !criado.data?.id) {
        setErro(criado.error || 'Não foi possível criar o organograma.');
        return;
      }

      const salvo = await organogramasService.saveCanvas(criado.data.id, montarPayloadCanvas(resultado))
        .catch((e: unknown) => ({ success: false, error: e instanceof Error ? e.message : undefined }));
      if (!salvo.success) {
        // Desfaz a criação para não sobrar um organograma vazio na lista. Se ele virou principal (empresa
        // sem nenhum), o backend só exclui depois de tirar o principal.
        const id = criado.data.id;
        if (criado.data.is_principal) await organogramasService.update(id, { is_principal: false }).catch(() => null);
        const desfeito = await organogramasService.delete(id).catch(() => null);
        if (desfeito?.success) {
          setErro(salvo.error || 'Não foi possível gravar os cards. Nada foi criado — tente gerar de novo.');
          return;
        }
        // Não deu para excluir: leva a pessoa até o organograma vazio em vez de deixá-lo órfão na lista.
        toast.error(salvo.error || 'O organograma foi criado, mas não foi possível gravar os cards.');
        onOpenChange(false);
        navigate(`/organograma/${id}`);
        return;
      }

      toast.success('Organograma gerado a partir dos cadastros');
      onOpenChange(false);
      navigate(`/organograma/${criado.data.id}`);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao gerar o organograma.');
    } finally {
      setIsGerando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Gerar organograma a partir dos cadastros</DialogTitle>
          <DialogDescription>
            Monta um organograma novo com os departamentos, cargos e gestores cadastrados. Nenhum organograma
            existente é alterado — depois de gerado, você pode editar o desenho à vontade.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Carregando cadastros...
          </div>
        )}

        {erro && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{erro}</span>
          </div>
        )}

        {resultado && dados && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="secondary">{dados.empresa.nome}</Badge>
              <Badge variant="outline">{resultado.avisos.totalPessoas} pessoa(s)</Badge>
              <Badge variant="outline">{totalDepartamentos} departamento(s) com card</Badge>
              <Badge variant="outline">{totalCards} card(s) no total</Badge>
            </div>

            {resultado.avisos.totalPessoas === 0 && (
              <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                {resultado.avisos.cargosPeloQuadro.length > 0
                  ? 'Nenhuma pessoa ativa cadastrada nesta empresa: o organograma terá a empresa e os cargos do quadro.'
                  : 'Nenhuma pessoa ativa encontrada nesta empresa. Será gerado apenas o card da empresa.'}
              </p>
            )}

            {avisos.length > 0 && (
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-medium">
                    Revisão (não impede a geração) · {avisos.length} {avisos.length === 1 ? 'aviso' : 'avisos'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Clique no nome de uma pessoa ou no atalho do aviso para ir até onde se corrige.
                  </p>
                </div>
                {/* Barra sempre visível quando há o que rolar (no macOS a nativa só aparece ao rolar). */}
                <ScrollArea barraQuandoTransborda rotuloRolagem="Avisos da revisão" className="max-h-64 rounded-lg border">
                  <ul className="divide-y">
                    {avisos.map((item) => (
                      <li key={item.chave} className="flex items-start gap-2 p-3 text-sm">
                        {item.informativo ? (
                          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        ) : (
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                        )}
                        <div className="min-w-0 space-y-0.5">
                          <p>{item.texto}</p>
                          {item.detalhe && <p className="text-xs text-muted-foreground">{item.detalhe}</p>}
                          {item.pessoas && item.pessoas.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {item.pessoas.slice(0, LIMITE_NOMES).map((pessoa, indice) => (
                                <Fragment key={`${item.chave}-${pessoa.usuarioId}`}>
                                  {indice > 0 && (item.separador ?? ', ')}
                                  <button
                                    type="button"
                                    className={CLASSE_ATALHO}
                                    title={`Abrir a ficha de ${pessoa.nome} para corrigir`}
                                    disabled={isGerando}
                                    onClick={() => irPara(fichaDoUsuario(pessoa.usuarioId))}
                                  >
                                    {pessoa.nome}
                                  </button>
                                </Fragment>
                              ))}
                              {item.pessoas.length > LIMITE_NOMES && (
                                <>
                                  {` e mais ${item.pessoas.length - LIMITE_NOMES} — `}
                                  <button
                                    type="button"
                                    className={CLASSE_ATALHO}
                                    disabled={isGerando}
                                    onClick={() => irPara('/usuarios')}
                                  >
                                    ver em Usuários
                                  </button>
                                </>
                              )}
                            </p>
                          )}
                          {item.acoes && item.acoes.length > 0 && (
                            <div className="flex flex-wrap gap-x-3">
                              {item.acoes.map((acao) => (
                                <button
                                  key={acao.destino}
                                  type="button"
                                  className={`inline-flex items-center gap-1 text-xs ${CLASSE_ATALHO}`}
                                  disabled={isGerando}
                                  onClick={() => irPara(acao.destino)}
                                >
                                  {acao.rotulo}
                                  <ArrowRight className="h-3 w-3" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="gerar-organograma-nome">Nome do organograma</Label>
              <Input
                id="gerar-organograma-nome"
                value={nome}
                onChange={(event) => setNome(event.target.value)}
                disabled={isGerando}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isGerando}>
            Cancelar
          </Button>
          <Button onClick={handleGerar} disabled={!resultado || !nome.trim() || isGerando}>
            {isGerando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
            {isGerando ? 'Gerando...' : 'Gerar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
